import { Theme, defineTheme } from '@astryxdesign/core/theme'
import { neutralTheme } from '@astryxdesign/theme-neutral'
import { useMemo, type ReactNode } from 'react'

import { useBusinessProfile } from '@/features/business/api/useBusinessProfile.ts'
import { BrandDuotone } from '@/components/brand/BrandDuotone.tsx'
import { brandVariables } from '@/lib/color/brandVariables.ts'

interface AstryxThemeProviderProps {
  readonly children: ReactNode
}

/** Fallback while the studio's own accent is still being read. */
const NEUTRAL_ACCENT: readonly [string, string] = ['#2563EB', '#5B9CFF']

/**
 * Astryx theme, seeded with the active studio's accent.
 *
 * Runtime `defineTheme` rather than a pre-built theme because the accent comes
 * from tenant configuration and is not known at build time. Astryx derives the
 * whole accent scale from the hex pair through HCT — including
 * `--color-on-accent` — which is why no per-tenant colour is hand-written and
 * no accent can ship without a readable foreground.
 *
 * The tenant pipeline should pre-build one stylesheet per studio with
 * `astryx theme build` so first paint stops paying for runtime injection.
 *
 * Mode is fixed to `dark`: dark is the approved product theme
 * (`design-system/autobooking/MASTER.md` §2). There is no theme switcher.
 */
export function AstryxThemeProvider({ children }: AstryxThemeProviderProps) {
  const { business } = useBusinessProfile()
  const accent = business?.accent ?? NEUTRAL_ACCENT

  const theme = useMemo(
    () =>
      defineTheme({
        name: 'autobooking',
        extends: neutralTheme,
        color: { accent: [accent[0], accent[1]], neutralStyle: 'cool' },
      }),
    [accent],
  )

  // The studio's own dark-mode hex, untouched. Decorative light (glows, the
  // hero duotone) always uses it as --brand. It also becomes the action colour
  // with black text — the reference's vivid button — but only when that pair
  // clears WCAG AA; otherwise --primary stays Astryx's contrast-derived accent.
  // Astryx lightens the accent for contrast, which reads lilac on black.
  const brandStyle = useMemo(() => brandVariables(accent[1]), [accent])

  return (
    <Theme theme={theme} mode="dark">
      <div style={brandStyle} className="contents">
        <BrandDuotone brand={accent[1]} />
        {children}
      </div>
    </Theme>
  )
}
